import { useCallback, useMemo } from 'react';

import { RetailerNetworkTobaccoProgramMetricType } from '@occc/graphql';
import { getDateRanges } from '@occc/ui-design-system';
import { useAdvancedQueryParam } from '@occc/utils-query-params';

import { useMetricsOverview } from './use-metrics-overview';

export function usePerformanceDetails(retailerNetworkUuid: string) {
  const dateRanges = useMemo(() => getDateRanges(), []);

  const metricDateRanges = useMemo(
    () => [
      dateRanges.today,
      dateRanges.last_7_days,
      dateRanges.last_14_days,
      dateRanges.last_30_days,
    ],
    [dateRanges],
  );

  const [activeMetricType, setActiveMetricType] =
    useAdvancedQueryParam<RetailerNetworkTobaccoProgramMetricType>({
      paramName: 'metricKey',
      allowedValues: Object.values(
        RetailerNetworkTobaccoProgramMetricType,
      ) as RetailerNetworkTobaccoProgramMetricType[],
      defaultValue: RetailerNetworkTobaccoProgramMetricType.LoyaltyTransactions,
    });

  const [startIso, setStartIso] = useAdvancedQueryParam<string>({
    paramName: 'startDate',
    defaultValue: dateRanges.last_30_days.startDate.toISOString(),
  });
  const startDate = useMemo(() => new Date(startIso), [startIso]);

  const [endIso, setEndIso] = useAdvancedQueryParam<string>({
    paramName: 'endDate',
    defaultValue: dateRanges.last_30_days.endDate.toISOString(),
  });
  const endDate = useMemo(() => new Date(endIso), [endIso]);

  const onDateChange = useCallback(
    (startDate: Date, endDate: Date) => {
      setStartIso(startDate.toISOString());
      setEndIso(endDate.toISOString());
    },
    [setStartIso, setEndIso],
  );

  const metricsOverview = useMetricsOverview({
    retailerNetworkUuid,
    startDate,
    endDate,
  });

  return {
    startDate,
    endDate,
    activeMetricType,
    setActiveMetricType,
    metricsOverview,
    metricDateRanges,
    onDateChange,
  };
}
